/**
 * 체험단 캠페인 생성 페이지 (고객사용)
 *
 * - 새 캠페인 생성
 * - 복제 모드: location.state.duplicateFrom 으로 기존 캠페인 데이터 pre-fill
 * - react-hook-form + zod 스키마로 폼 검증
 * - 공통 폼은 form/CampaignForm 사용
 */
import toast from 'react-hot-toast'
import { useQueryClient } from '@tanstack/react-query'
import {
  useCreateCampaign,
  usePublishCampaign,
  getListCampaignsQueryKey,
} from '@/api/generated/v2-seeding-campaigns/v2-seeding-campaigns'
import { getProductDetail } from '@/api/generated/v2-catalog/v2-catalog'
import { auth } from '@/lib/auth'
import { useShop } from '@/lib/shopContext'
import {
  DEFAULT_FIELD_CONFIG,
  type SeedingCampaignCreateRequest,
  type SeedingCampaignResponse,
  type MissionRound,
} from '@/types'
import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

// 폼 컴포넌트 (features/ 디렉토리로 이동됨)
import { CampaignForm } from '@/features/seeding/form/CampaignForm'
import { SeedingPublishGate, extractGateMessage } from '@/features/seeding/SeedingPublishGate'
import { buildProductSelection } from '@/features/seeding/form/ProductSelector'
import { dateStrToIso, isoToDateStr } from '@/features/seeding/form/utils'
import { DEFAULT_NOTICES } from '@/features/seeding/form/constants'
import { campaignFormSchema } from '@/features/seeding/form/schema'
import type { CampaignFormValues } from '@/features/seeding/form/schema'
import type { CampaignFormData, RoundFormData, ProductSelectionState } from '@/features/seeding/form/types'

export const Route = createFileRoute('/_dashboard/seeding-campaigns/new')({
  component: NewCampaign,
})

/** 생성 모드 기본 폼 값 */
const DEFAULT_FORM_VALUES: CampaignFormValues = {
  title: '',
  description: '',
  notices: DEFAULT_NOTICES,
  thumbnail_url: '',
  detail_images: [],
  delivery_method: 'cafe24_order',
  product_name: '',
  product_image_url: '',
  product_price: undefined,
  product_url: '',
  // 미입력 상태로 둔다. 0 으로 초기화하면 빈 칸이어야 할 자리에 0 이 찍혀서,
  // 사용자는 "채웠다"고 보는데 공개 시엔 "모집 인원을 입력해주세요"가 뜬다.
  recruit_count: undefined as unknown as number,
  recruit_start_at: '',
  recruit_end_at: '',
  selection_announce_at: '',
  campaign_end_at: '',
  social_media_channels: { blog: 'optional', instagram: 'optional' },
  mission_deadline_at: '',
  field_config: { ...DEFAULT_FIELD_CONFIG },
  extra_questions: [],
  login_required: false,
  auto_coupon: true,
  skip_shipping_coupon: false,
}

function NewCampaign() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  // shop 은 레이아웃이 useGetShop 으로 채운 컨텍스트에서 읽는다 (여기서 재조회하지 않는다).
  // 복제 pre-fill 이 쓰는 값은 primary_domain 하나뿐이라, effect 는 그것만 의존하게 한다
  // (shop 객체 참조가 바뀔 때마다 pre-fill effect 가 재실행되면 폼 입력을 덮어쓸 수 있다)
  const { shop } = useShop()
  const shopDomain = shop?.primary_domain ?? null
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  // 공개 게이트 안내 메시지(백엔드 422 detail) + 방금 생성된 draft 캠페인 id
  // (게이트가 뜨면 캠페인은 이미 draft로 저장된 상태 → 닫을 때 상세로 이동해 재공개 유도)
  const [gateMessage, setGateMessage] = useState<string | null>(null)
  const [gateCampaignId, setGateCampaignId] = useState<string | null>(null)

  // Orval mutation 훅 — 캠페인 생성 + 공개
  const createCampaignMutation = useCreateCampaign()
  const publishCampaignMutation = usePublishCampaign()
  // 복제 모드 중복 적용 방지
  const duplicateAppliedRef = useRef(false)

  // react-hook-form 인스턴스 — zod 스키마로 검증
  // zodResolver 타입 단언: zod v4와 @hookform/resolvers v5의 타입 호환 문제 우회
  // 런타임에서는 정상 작동 (resolvers가 _zod 프로퍼티로 v4 감지)
  const form = useForm<CampaignFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(campaignFormSchema as any) as any,
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onSubmit', // 제출 시에만 검증 (실시간 검증은 handleDateChange에서 별도 처리)
    // 자동 포커스는 CampaignForm 이 화면 배치 순서로 직접 한다.
    // rhf 는 register() 된 필드만 포커스할 수 있는데 이 폼은 상품·날짜를 setValue 로
    // 관리해서, 제목이 채워져 있으면 그것들을 건너뛰고 모집 인원으로 뛰어버린다.
    shouldFocusError: false,
  })

  // 초기 미션 차수 / 상품 선택 상태
  const [initialRounds, setInitialRounds] = useState<RoundFormData[]>([
    { title: '', guideline: '', guideline_files: [], deadline_at: '', mission_type: 'media' },
  ])
  const [initialProductSelection, setInitialProductSelection] = useState<Partial<ProductSelectionState>>({})
  const [initialUseProductImage, setInitialUseProductImage] = useState(false)

  // 인증 확인 + 복제 모드 처리 (shop 은 컨텍스트에서 읽으므로 로딩을 기다린다)
  useEffect(() => {
    const loadData = async () => {
      if (!auth.isAuthenticated()) {
        navigate({ to: '/' })
        return
      }
      try {
        // 복제 모드: state에 캠페인 데이터가 있으면 폼에 pre-fill
        const state = location.state as { duplicateFrom?: SeedingCampaignResponse } | undefined
        const src = state?.duplicateFrom
        if (src && !duplicateAppliedRef.current) {
          duplicateAppliedRef.current = true

          // 제품 관련 필드는 비워두고 나머지만 pre-fill
          const prefillData: Partial<CampaignFormValues> = {
            title: `${src.title} (사본)`,
            description: src.description || '',
            notices: src.notices || DEFAULT_NOTICES,
            thumbnail_url: src.thumbnail_url || '',
            detail_images: src.detail_images || [],
            delivery_method: src.delivery_method || 'cafe24_order',
            // 원본이 임시 저장 캠페인이면 값이 비어 있을 수 있다 (복제 대상은 draft 도 가능)
            product_name: src.product_no ? '' : (src.product_name ?? ''),
            product_image_url: src.product_no ? '' : (src.product_image_url || ''),
            product_price: src.product_no ? 0 : src.product_price,
            product_url: src.product_no ? '' : (src.product_url || ''),
            // 임시 저장 캠페인도 복제 대상이라 비어 있을 수 있다 — 0 을 넣으면
            // 채워진 것처럼 보이는데 공개 시엔 "입력해주세요"가 뜬다
            recruit_count: src.recruit_count ?? (undefined as unknown as number),
            recruit_start_at: isoToDateStr(src.recruit_start_at),
            recruit_end_at: isoToDateStr(src.recruit_end_at),
            selection_announce_at: isoToDateStr(src.selection_announce_at),
            social_media_channels: src.social_media_channels || {},
            mission_deadline_at: isoToDateStr(src.mission_deadline_at),
            field_config: src.field_config || { ...DEFAULT_FIELD_CONFIG },
            extra_questions: src.extra_questions || [],
            login_required: src.login_required,
            auto_coupon: src.auto_coupon || false,
            campaign_end_at: isoToDateStr(src.campaign_end_at ?? undefined),
          }

          // product_no가 있으면 Cafe24 API에서 최신 상품 정보 fetch
          if (src.product_no) {
            try {
              // Orval 생성 함수로 상품 상세 조회 (imperative 호출)
              const productResponse = await getProductDetail(src.product_no)
              // customInstance가 JSON body를 직접 반환하므로 래퍼 없이 접근
              const product = productResponse as any
              if (!('variants' in product)) throw new Error('상품 조회 실패')
              const variants = product.variants || []
              const sellingVariants = variants.filter((v: { selling: string; display: string }) => v.selling === 'T' && v.display === 'T')

              // buildProductSelection으로 상품 상태 계산
              if (src.product_variant_code && sellingVariants.length > 0) {
                const matched = sellingVariants.find((v: { variant_code: string }) => v.variant_code === src.product_variant_code)
                if (matched) {
                  const { formUpdate, productState } = buildProductSelection(
                    product, [matched], sellingVariants.length > 1, sellingVariants.length, { primary_domain: shopDomain }, false,
                  )
                  Object.assign(prefillData, formUpdate)
                  setInitialProductSelection(productState)
                }
                // 매칭 실패 시 상품 선택 모달은 사용자가 직접 열어야 함
              } else if (sellingVariants.length > 1) {
                const uniqueAmounts = new Set(sellingVariants.map((v: { additional_amount: number }) => v.additional_amount))
                if (uniqueAmounts.size <= 1) {
                  const { formUpdate, productState } = buildProductSelection(
                    product, sellingVariants, true, sellingVariants.length, { primary_domain: shopDomain }, false,
                  )
                  Object.assign(prefillData, formUpdate)
                  setInitialProductSelection(productState)
                }
                // 가격이 다른 경우 사용자가 직접 옵션을 선택해야 함
              } else {
                const { formUpdate, productState } = buildProductSelection(
                  product, null, false, sellingVariants.length, { primary_domain: shopDomain }, false,
                )
                Object.assign(prefillData, formUpdate)
                setInitialProductSelection(productState)
              }
            } catch (err) {
              // fetch 실패 -> 원본 데이터로 fallback
              console.warn('복제: 상품 정보 fetch 실패, 원본 데이터 사용', err)
              prefillData.product_name = src.product_name ?? ''
              prefillData.product_image_url = src.product_image_url || ''
              prefillData.product_price = src.product_price
              prefillData.product_url = src.product_url || ''
            }
          }

          // react-hook-form에 복제 데이터 설정
          form.reset({ ...DEFAULT_FORM_VALUES, ...prefillData })

          // 미션 차수 pre-fill
          if (src.mission_rounds && src.mission_rounds.length > 0) {
            setInitialRounds(src.mission_rounds.map((r) => ({
              title: r.title,
              guideline: r.guideline || '',
              guideline_files: r.guideline_files || [],
              deadline_at: isoToDateStr(r.deadline_at),
              mission_type: (r.mission_type || 'both') as 'media' | 'link' | 'both',
            })))
          }
        }
      } catch (err) {
        console.error('Failed to load shop data:', err)
        navigate({ to: '/' })
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [navigate, location.state, shopDomain])

  // 폼 제출 — 캠페인 생성
  // 반환값 = "저장이 실제로 됐는가". 캠페인 생성에 성공하면 publish 가 실패해도 true 다
  // (캠페인은 draft 로 남아 있다). CampaignForm 이 이 신호로 이탈 가드 기준선을 갱신해,
  // 게이트 다이얼로그를 닫을 때 거짓 경고가 뜨고 재저장으로 캠페인이 중복 생성되는 것을 막는다.
  const handleSubmit = async (
    formData: CampaignFormData,
    missionRounds: MissionRound[],
    publish?: boolean,
  ): Promise<boolean> => {
    setIsSaving(true)
    try {
      // 마감일이 정해진 차수 중 마지막 것. 임시 저장은 마감일 미정 차수를 허용한다
      const deadlineAt = missionRounds
        .map((r) => r.deadline_at)
        .filter((d): d is string => !!d)
        .sort()
        .pop()

      // 임시 저장은 제목 외에 무엇이든 비어 있을 수 있다.
      // 빈 값은 undefined 로 보내 서버가 "미입력"으로 저장하게 한다
      // (빈 문자열을 날짜로 변환하면 Invalid Date 라 요청 자체가 터진다)
      const requestData: SeedingCampaignCreateRequest = {
        title: formData.title!,
        description: formData.description,
        notices: formData.notices || undefined,
        detail_images: formData.detail_images?.length ? formData.detail_images : undefined,
        delivery_method: formData.delivery_method || 'cafe24_order',
        product_name: formData.product_name || undefined,
        product_price: formData.product_price,
        product_url: formData.product_url || undefined,
        thumbnail_url: formData.thumbnail_url,
        product_image_url: formData.product_image_url,
        product_variant_code: formData.product_variant_code || undefined,
        allowed_variants: formData.allowed_variants || undefined,
        recruit_count: formData.recruit_count || undefined,
        recruit_start_at: dateStrToIso(formData.recruit_start_at, '00:00:00'),
        recruit_end_at: dateStrToIso(formData.recruit_end_at),
        selection_announce_at: dateStrToIso(formData.selection_announce_at),
        social_media_channels: formData.social_media_channels,
        mission_deadline_at: deadlineAt,
        mission_rounds: missionRounds,
        campaign_end_at: dateStrToIso(formData.campaign_end_at),
        field_config: formData.field_config || DEFAULT_FIELD_CONFIG,
        extra_questions: (formData.extra_questions && formData.extra_questions.length > 0)
          ? formData.extra_questions
          : undefined,
        login_required: formData.login_required ?? true,
        auto_coupon: formData.delivery_method === 'self_delivery' ? false : (formData.auto_coupon || false),
        skip_shipping_coupon: formData.skip_shipping_coupon || false,
        product_no: formData.product_no || undefined,
      }

      // Orval mutation으로 캠페인 생성
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const createResponse = await createCampaignMutation.mutateAsync({ data: requestData as any })
      // custom-instance가 data만 반환하므로 직접 캐스팅
      const campaign = createResponse as unknown as SeedingCampaignResponse

      // 생성은 성공했으니 목록 캐시를 먼저 무효화 (공개 실패해도 draft가 목록에 반영되도록)
      queryClient.invalidateQueries({ queryKey: getListCampaignsQueryKey() })

      if (publish) {
        try {
          await publishCampaignMutation.mutateAsync({ campaignId: campaign.id })
        } catch (err: unknown) {
          // 연락처 미등록 게이트(422)면 안내 다이얼로그로 등록을 유도한다.
          // 캠페인은 이미 draft로 저장돼 있으므로, 닫을 때 상세 페이지로 이동해 재공개하도록 유도한다.
          const gateMsg = extractGateMessage(err)
          if (gateMsg) {
            setGateCampaignId(campaign.id)
            setGateMessage(gateMsg)
            // 공개는 실패했지만 **캠페인은 저장됐다** — true 로 알려야 폼이 기준선을
            // 갱신하고, 게이트를 닫을 때 거짓 이탈 경고 → 재저장 → 중복 생성으로
            // 이어지지 않는다.
            return true
          }
          // 그 밖의 공개 실패(내용 미완성 등) — 캠페인은 이미 draft 로 저장됐다.
          // 이 폼에 머물게 하면 사용자가 다시 저장을 눌러 캠페인이 하나 더 생긴다.
          // 상세로 보내면 "공개하려면 아직 필요해요" 배너가 이유를 짚어준다.
          const detail = (err as { detail?: string; message?: string } | null)
          toast.error(detail?.detail || detail?.message || '공개에 실패했어요. 내용을 확인해주세요.')
        }
      }

      navigate({
        to: '/seeding-campaigns/$campaignId',
        params: { campaignId: campaign.id },
      })
      return true
    } finally {
      setIsSaving(false)
    }
  }

  // 게이트 다이얼로그를 닫으면, 방금 생성된 draft 캠페인 상세로 이동한다 (연락처 등록 후 재공개)
  const handleGateClose = () => {
    const targetId = gateCampaignId
    setGateMessage(null)
    setGateCampaignId(null)
    if (targetId) {
      navigate({ to: '/seeding-campaigns/$campaignId', params: { campaignId: targetId } })
    }
  }

  // 로딩 중 (shop이 아직 없어도 동일하게 skeleton 노출)
  if (isLoading || !shop) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-surface-high rounded w-48 mb-6"></div>
          <div className="h-96 bg-surface-high rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-on-surface">새 캠페인 만들기</h1>
          <p className="mt-1 text-on-surface-variant">체험단 캠페인을 생성합니다.</p>
        </div>
      </div>

      <CampaignForm
        mode="create"
        form={form}
        shop={shop}
        initialRounds={initialRounds}
        initialProductSelection={initialProductSelection}
        initialUseProductImage={initialUseProductImage}
        onSubmit={handleSubmit}
        onCancel={() => navigate({ to: '/seeding-campaigns' })}
        isSaving={isSaving}
      />

      {/* 공개 게이트 안내 — 연락처 미등록 시 등록 유도 (닫으면 생성된 draft 상세로 이동) */}
      <SeedingPublishGate message={gateMessage} onClose={handleGateClose} />
    </div>
  )
}
